import { ArrowRight, ChevronDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

function AIPreviewCard() {
  return (
    <div className="relative">
      <div className="absolute inset-0 scale-90 translate-y-4 bg-emerald-500/15 rounded-2xl blur-3xl pointer-events-none" />
      <div className="relative bg-slate-900 border border-slate-700/60 rounded-2xl overflow-hidden shadow-2xl shadow-black/60">
        <div className="flex items-center gap-2 px-4 py-3 bg-slate-800/80 border-b border-slate-700/60">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500/70" />
            <div className="w-3 h-3 rounded-full bg-amber-500/70" />
            <div className="w-3 h-3 rounded-full bg-emerald-500/70" />
          </div>
          <span className="text-slate-400 text-xs ml-2">Trading Journal — AI Coach</span>
        </div>

        <div className="p-5 space-y-3">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
              <span className="text-emerald-300 text-xs font-bold uppercase tracking-wider">Confirmed Edge</span>
            </div>
            <p className="text-slate-200 text-sm font-medium mb-1">NVDA long on earnings dips</p>
            <p className="text-slate-400 text-xs leading-relaxed">8 of 9 trades profitable · avg +$620 · your strongest setup this quarter</p>
          </div>

          <div className="bg-red-500/8 border border-red-500/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-red-300 text-xs font-bold uppercase tracking-wider">Revenge Trading</span>
              </div>
              <span className="text-red-400 text-xs font-bold">$4,200 cost</span>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">23 trades within 30 min of a loss over $300 — 19 were losing</p>
          </div>

          <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                <span className="text-amber-300 text-xs font-bold uppercase tracking-wider">Time Blindspot</span>
              </div>
              <span className="text-amber-400 text-xs font-bold">31% win rate</span>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">9:30–10:00 AM is killing you — 67% win rate at all other times</p>
          </div>

          <div className="text-center pt-1">
            <span className="text-slate-600 text-xs italic">Sample · generated from your real trade history</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection({ isAuthenticated }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-slate-700/10 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(rgba(16,185,129,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-8">
            <TrendingUp size={14} className="text-emerald-400" />
            <span className="text-emerald-300 text-xs font-semibold tracking-wide uppercase">Free for Active Investors</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            <span className="text-white">Know Your Edge.</span>
            <br />
            <span className="text-white">Fix Your </span>
            <span className="text-emerald-400">Behavior.</span>
            <br />
            <span className="text-white">Trade Better.</span>
          </h1>

          <p className="text-xl text-slate-300 leading-relaxed mb-3">
            The only trading journal that finds the behavioral patterns costing you money — before they blow your account.
          </p>
          <p className="text-slate-500 text-sm italic mb-8">
            By the author of{' '}
            <button
              onClick={() => scrollTo('book')}
              className="text-amber-400 hover:text-amber-300 transition-colors not-italic underline underline-offset-2"
            >
              Wealth At Any Age
            </button>{' '}
            — built to put the book's strategies into daily practice.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10">
            {isAuthenticated ? (
              <Link
                to="/"
                className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-4 rounded-xl transition-all text-lg shadow-lg shadow-emerald-500/25"
              >
                Open Dashboard <ArrowRight size={16} />
              </Link>
            ) : (
              <>
                <Link
                  to="/login?mode=register"
                  className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-4 rounded-xl transition-all text-lg shadow-lg shadow-emerald-500/25"
                >
                  Start Free — No Credit Card <ArrowRight size={16} />
                </Link>
                <button
                  onClick={() => scrollTo('features')}
                  className="flex items-center justify-center gap-2 border border-slate-600 hover:border-slate-400 text-slate-300 hover:text-white font-semibold px-8 py-4 rounded-xl transition-all text-lg"
                >
                  See how it works <ChevronDown size={16} />
                </button>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><span className="text-emerald-400">✓</span> Free broker sync (Alpaca, Schwab)</span>
            <span className="flex items-center gap-1.5"><span className="text-emerald-400">✓</span> AI edge analysis included</span>
            <span className="flex items-center gap-1.5"><span className="text-emerald-400">✓</span> Privacy-first · your data stays yours</span>
          </div>
        </div>

        <AIPreviewCard />
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600 animate-bounce">
        <span className="text-xs">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-slate-600 to-transparent" />
      </div>
    </div>
  );
}
